import { useState, useEffect, useCallback, useMemo } from 'react';
import { Website, WebsiteSummary, WebsiteFilters, CreateWebsiteRequest, UpdateWebsiteRequest, WebsiteTestResult } from '../types/website';
import { websiteService } from '../../../../api/cansense/websiteApi';

interface UseWebsitesReturn {
  websites: Website[];
  summary: WebsiteSummary;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createWebsite: (data: CreateWebsiteRequest) => Promise<{ success: boolean; website: Website; test_result?: WebsiteTestResult }>;
  updateWebsite: (id: number, data: UpdateWebsiteRequest) => Promise<Website>;
  deleteWebsite: (id: number) => Promise<void>;
  pauseWebsite: (id: number) => Promise<Website>;
  resumeWebsite: (id: number) => Promise<Website>;
  testUrl: (url: string, probeType?: string) => Promise<WebsiteTestResult>;
}

const defaultSummary: WebsiteSummary = {
  total: 0,
  up: 0,
  down: 0,
  paused: 0,
  avg_response_time_ms: null,
  ssl_expiring_soon: 0,
};

export const useWebsites = (filters?: WebsiteFilters): UseWebsitesReturn => {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [summary, setSummary] = useState<WebsiteSummary>(defaultSummary);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const stableFilters = useMemo(() => filters, [
    filters?.status,
    filters?.customer_id,
    filters?.search,
  ]);

  const fetchWebsites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await websiteService.getWebsites(stableFilters);
      setWebsites(data.websites);
      setSummary(data.summary);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch websites';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [stableFilters]);

  useEffect(() => {
    fetchWebsites();
  }, [fetchWebsites]);

  const createWebsite = useCallback(async (data: CreateWebsiteRequest) => {
    const result = await websiteService.createWebsite(data);
    await fetchWebsites();
    return result;
  }, [fetchWebsites]);

  const updateWebsite = useCallback(async (id: number, data: UpdateWebsiteRequest) => {
    const result = await websiteService.updateWebsite(id, data);
    await fetchWebsites();
    return result;
  }, [fetchWebsites]);

  const deleteWebsite = useCallback(async (id: number) => {
    await websiteService.deleteWebsite(id);
    await fetchWebsites();
  }, [fetchWebsites]);

  const pauseWebsite = useCallback(async (id: number) => {
    const result = await websiteService.pauseWebsite(id);
    await fetchWebsites();
    return result;
  }, [fetchWebsites]);

  const resumeWebsite = useCallback(async (id: number) => {
    const result = await websiteService.resumeWebsite(id);
    await fetchWebsites();
    return result;
  }, [fetchWebsites]);

  const testUrl = useCallback(async (url: string, probeType?: string, port?: number) => {
    return websiteService.testUrl(url, probeType, port);
  }, []);

  return {
    websites, summary, loading, error, refetch: fetchWebsites,
    createWebsite, updateWebsite, deleteWebsite,
    pauseWebsite, resumeWebsite, testUrl,
  };
};
